import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater, type BundleInfo } from '@capgo/capacitor-updater';
import {checkForUpdate, type AppUpdate, type NativePlatform} from '@/api/updatesApi';
import { compareVersions, isNewerOtaVersion } from '@/utils/otaVersion';
const pendingVersionKey = 'ota-pending-version';

export async function getInstalledAppVersion(): Promise<{version: string; source: 'ota' | 'builtin'} | null> {
  if (!Capacitor.isNativePlatform()) return null;

  const nativeVersion = (await App.getInfo()).version;
  try {
    const {bundle} = await CapacitorUpdater.current();
    if (bundle.id !== 'builtin' && bundle.version) {
      return {version: bundle.version, source: 'ota'};
    }
  } catch (error) {
    console.warn('[OTA] unable to read the installed bundle:', error);
  }

  return {version: nativeVersion, source: 'builtin'};
}

export async function downloadUpdate(update: AppUpdate): Promise<BundleInfo> {
  console.info(`[OTA] downloading ${update.version}`);
  const bundle = await CapacitorUpdater.download({
    url: update.url,
    version: update.version,
    checksum: update.checksum,
  });
  if (!bundle.id || bundle.version !== update.version || bundle.status === 'error') {
    throw new Error('Downloaded bundle failed validation');
  }

  return bundle;
}

export async function queueUpdate(bundle: BundleInfo): Promise<void> {
  localStorage.setItem(pendingVersionKey, bundle.version);
  try {
    await CapacitorUpdater.setMultiDelay({ delayConditions: [{ kind: 'kill' }] });
    await CapacitorUpdater.next({ id: bundle.id });
    console.info('[OTA] update queued for the next app launch');
  } catch (error) {
    localStorage.removeItem(pendingVersionKey);
    throw error;
  }
}

async function runUpdateCheck(platform: NativePlatform): Promise<void> {
  const update = await checkForUpdate(platform);
  if (!update) return;

  console.info(`[OTA] available version: ${update.version}`);
  if (update.min_native_version !== null) {
    const nativeVersion = (await App.getInfo()).version;
    const compatibility = compareVersions(nativeVersion, update.min_native_version);
    if (compatibility === null) {
      console.warn('[OTA] native version is not semantic; skipping update');
      return;
    }
    if (compatibility < 0) {
      console.info('[OTA] skipped because native version is too old');
      return;
    }
  }

  const { bundle } = await CapacitorUpdater.current();
  const currentVersion = bundle.id === 'builtin' ? null : bundle.version;
  console.info(`[OTA] current bundle: ${currentVersion ?? 'builtin'}`);

  if (!isNewerOtaVersion(update.version, currentVersion)) {
    console.info('[OTA] no newer update available');
    return;
  }

  // Native rollback returns to the old bundle. Avoid reinstalling that failed version.
  if (localStorage.getItem(pendingVersionKey) === update.version) {
    console.warn('[OTA] skipped a bundle that did not become ready after activation');
    return;
  }

  await queueUpdate(await downloadUpdate(update));
}

let initialization: Promise<void> | undefined;

export function initAppUpdater(): Promise<void> {
  initialization ??= initialize();
  return initialization;
}

async function initialize(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  const platform = Capacitor.getPlatform();
  if (platform !== 'ios' && platform !== 'android') return;

  try {
    // Mark the mounted bundle healthy before doing any network work.
    const ready = await CapacitorUpdater.notifyAppReady();
    console.info('[OTA] updater initialized');
    if (ready.bundle.id !== 'builtin' &&
      localStorage.getItem(pendingVersionKey) === ready.bundle.version) {
      localStorage.removeItem(pendingVersionKey);
    }
  } catch (error) {
    console.error('[OTA] notifyAppReady failed:', error);
    return;
  }

  if (import.meta.env.VITE_OTA_UPDATES_ENABLED !== 'true') return;

  try {
    await runUpdateCheck(platform);
  } catch (error) {
    console.warn('[OTA] update failed; continuing with the current bundle:', error);
  }
}
